import md5 from 'md5';
import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import CreativeFilePlacementType from '@/common/enums/CreativeFilePlacementType.enum';
import CreativeFileTagType from '@/common/enums/CreativeFileTagType.enum';
import CreativeType from '@/common/enums/CreativeType.enum';
import CreativeFilePlacementTypeOptions from '@/common/models/creative/CreativeFilePlacementTypeOptions';
import CreativeFileTagTypeOptions from '@/common/models/creative/CreativeFileTagTypeOptions';
import EntityProperty from '@/common/models/EntityProperty';
import { DASH } from '@/globals';
import Validatable, { IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

const type = 'CreativeType';

export enum CreativeThumbnailSuffix {
  Image = '-thumbnail.0000000.jpg',
  Video = '-preview.mp4',
}

export interface ICreativeFileAPI {
  creativefile_id: number;
  name: string;
  uuid: string;
  creative_id: number;
  width: number;
  height: number;
  url: string;
  format: string;
  duration: number;
  prefetch_approval: string;
  file_size: number;
  // Note: prefetch_is_external is read-only.
  prefetch_is_external: boolean;
  archived: boolean;
  generated: boolean; // to know whether the file was generated by the creative resizing feature or not
  tag_type?: CreativeFileTagType;
  placement_type?: CreativeFilePlacementType;
  tag_content?: string;
  is_vast?: boolean;
}

interface IGeneratedCreativeFile {
  filename: string;
  width: number;
  height: number;
  format: string;
  base64: string;
  url: string;
}

export default class CreativeFile extends Validatable {
  name: string;
  uuid: string;
  creativeID: number;
  width: number;
  height: number;
  url: string;
  format: string;
  duration: number;
  prefetchApproval: string;
  prefetchIsExternal: boolean;
  fileSize: number;
  isArchived: boolean;
  isGenerated: boolean;
  tagType?: CreativeFileTagType;
  placementType?: CreativeFilePlacementType;
  tagContent?: string;
  isVast?: boolean;

  constructor(
    id = -1,
    name: string,
    uuid: string,
    creativeID: number,
    width: number,
    height: number,
    url: string,
    format: string,
    duration: number,
    prefetchApproval: string,
    prefetchIsExternal: boolean,
    fileSize: number,
    isArchived: boolean,
    isGenerated: boolean = false,
    tagType: CreativeFileTagType = undefined,
    placementType: CreativeFilePlacementType = undefined,
    tagContent: string = undefined,
    isVast: boolean = false,
  ) {
    super();
    this.id = id;
    this.name = name;
    this.uuid = uuid;
    this.creativeID = creativeID;
    this.width = width;
    this.height = height;
    this.url = url;
    this.format = format;
    this.duration = duration;
    this.prefetchApproval = prefetchApproval;
    this.prefetchIsExternal = prefetchIsExternal;
    this.fileSize = fileSize;
    this.isArchived = isArchived;
    this.isGenerated = isGenerated;
    this.tagType = tagType;
    this.placementType = placementType;
    this.tagContent = tagContent;
    this.isVast = isVast;
  }

  get endpoint(): string {
    return `/creativefiles/${this.id}`;
  }

  get prefetchApprovalDescription(): string {
    switch (this.prefetchApproval) {
      case 'A':
        return capitalize(i18n.global.t('states.approved'));
      case 'P':
        return capitalize(i18n.global.t('states.pendingApproval'));
      case 'R':
        return capitalize(i18n.global.t('states.rejected'));
      default:
        return DASH;
    }
  }

  get pendingApprovalEndpoint(): string {
    return this.prefetchIsExternal
      ? `/pendingapprovals/externalcreatives/${this.creativeID}`
      : `/pendingapprovals/creatives/${this.creativeID}`;
  }

  get idAndName(): string {
    return `${this.id} - ${this.name}`;
  }

  get isVideo(): boolean {
    return this.format.includes('video');
  }

  get isImage(): boolean {
    return this.format.includes('image');
  }

  get isHtml(): boolean {
    return this.format.includes('html');
  }

  get isZip(): boolean {
    return this.format.includes('zip');
  }

  get isAudio(): boolean {
    return this.format.includes('audio');
  }

  // placement types are a display/video only property and is required for DV creative file
  get isDisplayVideo(): boolean {
    return !!this.placementType;
  }

  get imageThumbnailUrl(): string {
    // removes file extension at the end of the url
    return this.url ? `${this.url.substr(0, this.url.lastIndexOf('.'))}${CreativeThumbnailSuffix.Image}` : null;
  }

  get macros(): string[] {
    if (!this.isVast || !this.url) return [];

    const matches = this.url.match(/\[([^\]]+)\]/g);
    return matches ? matches.map((match) => match.slice(1, -1)) : [];
  }

  get videoPreviewUrl(): string {
    // removes file extension at the end of the url
    return this.url ? `${this.url.substr(0, this.url.lastIndexOf('.'))}${CreativeThumbnailSuffix.Video}` : null;
  }

  get hash(): string {
    return `${this.id}-${md5(this.tagContent || this.url)}`;
  }

  get placementTypeFormatted(): string {
    return CreativeFilePlacementTypeOptions.options().find((x) => x.value === this.placementType)?.name || DASH;
  }

  get tagTypeFormatted(): string {
    return CreativeFileTagTypeOptions.options().find((x) => x.value === this.tagType)?.name || DASH;
  }

  get contentFormatted(): string {
    return this.tagContent?.length > 50 ? this.tagContent.slice(0, 50).concat('...') : this.tagContent || DASH;
  }

  getExtraValidation(): { [key: string]: { message: string; preEvaluated: boolean } } {
    const extras: { [key: string]: { message: string; preEvaluated: boolean } } = {};
    if (this.name.length > 100) {
      extras.name = {
        message: i18n.global.t('messages.creativeFileNameTooBigDescription'),
        preEvaluated: true,
      };
    }
    return extras;
  }

  static fromApi(apiObject: ICreativeFileAPI): CreativeFile {
    return new CreativeFile(
      apiObject.creativefile_id,
      apiObject.name,
      apiObject.uuid,
      apiObject.creative_id,
      apiObject.width,
      apiObject.height,
      apiObject.url,
      apiObject.format,
      apiObject.duration,
      apiObject.prefetch_approval,
      apiObject.prefetch_is_external,
      apiObject.file_size,
      apiObject.archived,
      apiObject.generated,
      apiObject.tag_type,
      apiObject.placement_type,
      apiObject.tag_content,
      apiObject.is_vast,
    );
  }

  toApi(): ICreativeFileAPI {
    return {
      creativefile_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      uuid: this.uuid,
      creative_id: this.creativeID,
      width: this.width,
      height: this.height,
      url: this.url,
      format: this.format,
      duration: this.duration,
      prefetch_approval: this.prefetchApproval,
      file_size: this.fileSize,
      // Note: prefetch_is_external is read-only.
      prefetch_is_external: undefined,
      archived: undefined,
      generated: this.isGenerated,
      tag_type: this.tagType,
      placement_type: this.placementType,
      tag_content: this.tagContent,
      is_vast: this.isVast,
    };
  }

  static validatorCreateRules(): Record<string, string> {
    return {
      name: 'required|max:250',
      url: 'url_with_macros',
    };
  }

  static validatorUpdateRules(): { [key: string]: string } {
    return {
      name: 'required|max:250',
      url: 'url_with_macros',
    };
  }

  getExtraValidationRules(creativeType: CreativeType = null) {
    let rules: Record<string, IExtraValidation>;
    if (creativeType === CreativeType.Video) {
      // Width and height are not mandatory for tag creative files in a creative with the "Video" type.
      rules = {
        width: { value: this.width, rules: 'required|numeric:int|min_value:0' },
        height: { value: this.height, rules: 'required|numeric:int|min_value:0' },
      };
    } else {
      rules = {
        width: { value: this.width, rules: 'required|numeric:int|min_value:1' },
        height: { value: this.height, rules: 'required|numeric:int|min_value:1' },
      };
    }
    return rules;
  }

  /**
   * Generated files are files that went through the /creative/resize endpoint
   * @param generatedFile IGeneratedCreativeFile
   * @returns CreativeFile
   */
  static fromGeneratedFile(generatedFile: IGeneratedCreativeFile): CreativeFile {
    return new CreativeFile(
      -1,
      `${generatedFile.filename}`,
      '',
      -1,
      generatedFile.width,
      generatedFile.height,
      generatedFile.url,
      generatedFile.format,
      0,
      'P',
      false,
      0,
      false,
      true,
    );
  }

  static get entityProperties() {
    if (CreativeFile.entityPropertiesCached != null) {
      return CreativeFile.entityPropertiesCached;
    }

    CreativeFile.entityPropertiesCached = {
      id: new EntityProperty({
        label: '#',
        property: 'creative_id',
        type: TableColumnTypes.string,
      }),
      name: new EntityProperty({
        label: capitalize(i18n.global.t('names.name')),
        property: 'name',
        type: TableColumnTypes.string,
      }),
      uuid: new EntityProperty({
        label: capitalize(i18n.global.t('names.uuid')),
        property: 'uuid',
        type: TableColumnTypes.string,
      }),
      width: new EntityProperty({
        label: capitalize(i18n.global.t('names.width')),
        property: 'width',
        type: TableColumnTypes.integer,
      }),
      height: new EntityProperty({
        label: capitalize(i18n.global.t('names.height')),
        property: 'height',
        type: TableColumnTypes.integer,
      }),
      url: new EntityProperty({
        label: capitalize(i18n.global.t('names.url')),
        property: 'url',
        type: TableColumnTypes.string,
      }),
      format: new EntityProperty({
        label: capitalize(i18n.global.t('names.format')),
        property: 'format',
        type: TableColumnTypes.string,
      }),
      duration: new EntityProperty({
        label: capitalize(i18n.global.t('names.duration')),
        property: 'duration',
        type: TableColumnTypes.float,
      }),
      fileSize: new EntityProperty({
        label: capitalize(i18n.global.t('names.fileSize')),
        property: 'file_size',
        type: TableColumnTypes.integer,
      }),
      hash: new EntityProperty({
        label: capitalize(i18n.global.t('names.hash')),
        property: 'hash',
        type: TableColumnTypes.string,
      }),
      isGenerated: new EntityProperty({
        label: capitalize(i18n.global.t('messages.generated')),
        property: 'generated',
        type: TableColumnTypes.boolean,
      }),
      tagContent: new EntityProperty({
        label: capitalize(i18n.global.t('names.content')),
        property: 'tag_content',
        type: TableColumnTypes.string,
      }),
      placementType: new EntityProperty({
        label: capitalize(i18n.global.t('names.placementType')),
        property: 'placement_type',
        type: TableColumnTypes.list,
        editablePropertyName: 'placementType',
        editableClassName: type,
        source: () =>
          CreativeFilePlacementTypeOptions.options().map((x) => ({
            id: x.value,
            name: x.name,
          })),
      }),
      tagType: new EntityProperty({
        label: capitalize(i18n.global.t('names.tagType')),
        property: 'tag_type',
        type: TableColumnTypes.list,
        editablePropertyName: 'tagType',
        editableClassName: type,
        source: () =>
          CreativeFileTagTypeOptions.options().map((x) => ({
            id: x.value,
            name: x.name,
          })),
      }),
    };

    return CreativeFile.entityPropertiesCached;
  }
}
